import React, { useState } from "react";
import { Upload, Check, AlertCircle, User, FileText, Shield } from "lucide-react";

import { useTranslation } from 'react-i18next';
export default function KYCForm({ onSubmit, onCancel }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1 — Basic info
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    cpf: "",
    birthDate: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  // Step 2 — Document upload (mock)
  const [documents, setDocuments] = useState({
    idFront: null,
    idBack: null,
    selfie: null,
    proofOfAddress: null,
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (docType) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocuments((prev) => ({ ...prev, [docType]: file.name }));
    }
  };

  const step1Valid = formData.fullName && formData.email && formData.phone && formData.cpf;

  const step2Valid = documents.idFront && documents.selfie;

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (step1Valid) {
      setStep(2);
    }
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    if (!step2Valid) return;
    setLoading(true);
    setError(null);
    // Mock upload
    setTimeout(() => {
      setLoading(false);
      onSubmit?.({ ...formData, documents: Object.keys(documents).filter((k) => documents[k]) });
    }, 2000);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else onCancel?.();
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
      {/* Step indicator */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold ${
            step >= 1 ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-500"
          }`}>
            {step > 1 ? <Check className="w-4 h-4" /> : "1"}
          </div>
          <span className={`text-xs font-mono uppercase tracking-wider ${step === 1 ? "text-amber-400" : "text-zinc-500"}`}>{t('k_y_c_form.basic_info')}</span>
        </div>
        <div className="flex-1 h-px bg-zinc-800" />
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold ${
            step >= 2 ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-500"
          }`}>
            {step > 2 ? <Check className="w-4 h-4" /> : "2"}
          </div>
          <span className={`text-xs font-mono uppercase tracking-wider ${step === 2 ? "text-amber-400" : "text-zinc-500"}`}>{t('k_y_c_form.documentos')}</span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-sm mb-4">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Step 1 — Basic info */}
      {step === 1 && (
        <form onSubmit={handleStep1Submit}>
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-display uppercase text-white">{t('k_y_c_form.personal_information')}</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => updateField("fullName", e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white text-sm focus:outline-none focus:border-amber-500"
                placeholder={t('k_y_c_form.your_full_name')}
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white text-sm focus:outline-none focus:border-amber-500"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-1">
                Phone *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white text-sm focus:outline-none focus:border-amber-500"
                placeholder="(11) 99999-9999"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-1">
                CPF *
              </label>
              <input
                type="text"
                value={formData.cpf}
                onChange={(e) => updateField("cpf", e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white text-sm focus:outline-none focus:border-amber-500"
                placeholder="000.000.000-00"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-1">{t('k_y_c_form.date_of_birth')}</label>
              <input
                type="date"
                value={formData.birthDate}
                onChange={(e) => updateField("birthDate", e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-1">{t('k_y_c_form.address')}</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField("address", e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white text-sm focus:outline-none focus:border-amber-500"
                placeholder={t('k_y_c_form.street_number')}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
            <button
              type="button"
              onClick={handleBack}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors px-3 py-2"
            >
              {onCancel ? "Cancel" : "Back"}
            </button>
            <button
              type="submit"
              disabled={!step1Valid}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black px-6 py-2.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >{t('k_y_c_form.next')}</button>
          </div>
        </form>
      )}

      {/* Step 2 — Document upload */}
      {step === 2 && (
        <form onSubmit={handleStep2Submit}>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-display uppercase text-white">{t('k_y_c_form.document_submission')}</h3>
          </div>

          <p className="text-zinc-400 text-sm mb-4">{t('k_y_c_form.upload_a_photo_of_your_id_document_and_a_selfie')}</p>

          <div className="space-y-4 mb-6">
            {/* ID Front */}
            <div className={`border rounded-sm p-4 ${documents.idFront ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900"}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
                  {documents.idFront ? <Check className="w-5 h-5 text-emerald-400" /> : <Upload className="w-5 h-5 text-zinc-500" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">Front of Document *</div>
                  <div className="text-xs text-zinc-500">{documents.idFront || "ID or Driver's License — JPG or PNG"}</div>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFileChange("idFront")}
                  className="hidden"
                />
                <span className="text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-400">
                  {documents.idFront ? "Replace" : "Choose"}
                </span>
              </label>
            </div>

            {/* ID Back */}
            <div className={`border rounded-sm p-4 ${documents.idBack ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900"}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
                  {documents.idBack ? <Check className="w-5 h-5 text-emerald-400" /> : <Upload className="w-5 h-5 text-zinc-500" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">Back of Document</div>
                  <div className="text-xs text-zinc-500">{documents.idBack || "Optional — JPG or PNG"}</div>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFileChange("idBack")}
                  className="hidden"
                />
                <span className="text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-400">
                  {documents.idBack ? "Replace" : "Choose"}
                </span>
              </label>
            </div>

            {/* Selfie */}
            <div className={`border rounded-sm p-4 ${documents.selfie ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900"}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
                  {documents.selfie ? <Check className="w-5 h-5 text-emerald-400" /> : <Shield className="w-5 h-5 text-zinc-500" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">Selfie with Document *</div>
                  <div className="text-xs text-zinc-500">{documents.selfie || "Hold the document next to your face"}</div>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFileChange("selfie")}
                  className="hidden"
                />
                <span className="text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-400">
                  {documents.selfie ? "Replace" : "Choose"}
                </span>
              </label>
            </div>

            {/* Proof of Address */}
            <div className={`border rounded-sm p-4 ${documents.proofOfAddress ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900"}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
                  {documents.proofOfAddress ? <Check className="w-5 h-5 text-emerald-400" /> : <Upload className="w-5 h-5 text-zinc-500" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">Proof of Address</div>
                  <div className="text-xs text-zinc-500">{documents.proofOfAddress || "Utility bill — up to 3 months"}</div>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,.pdf"
                  onChange={handleFileChange("proofOfAddress")}
                  className="hidden"
                />
                <span className="text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-400">
                  {documents.proofOfAddress ? "Replace" : "Choose"}
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
            <button
              type="button"
              onClick={handleBack}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors px-3 py-2"
            >{t('k_y_c_form.back')}</button>
            <button
              type="submit"
              disabled={!step2Valid || loading}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black px-6 py-2.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >
              {loading ? "Sending..." : "Submit for Verification"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
